import React from 'react';
import { Move, Sparkles, Maximize2, Layers, Clock, Palette, Play } from 'lucide-react';

const TFS = ['1m', '3m', '5m', '15m', '30m', '1h', '2h', '4h', '1d', '1w', '1M'];
const TYPES = [
  { k: 'candles',       label: 'CANDLES' },
  { k: 'heikin_ashi',   label: 'HEIKIN' },
  { k: 'line',          label: 'LINE' },
  { k: 'area',          label: 'AREA' },
];
const IND = [
  { k: 'ema9',       label: 'EMA 9' },
  { k: 'ema20',      label: 'EMA 20' },
  { k: 'ema21',      label: 'EMA 21' },
  { k: 'ema50',      label: 'EMA 50' },
  { k: 'ema100',     label: 'EMA 100' },
  { k: 'ema200',     label: 'EMA 200' },
  { k: 'ema300',     label: 'EMA 300' },
  { k: 'ema500',     label: 'EMA 500' },
  { k: 'vwap',       label: 'VWAP' },
  { k: 'bb',         label: 'BB' },
  { k: 'dbb',        label: 'DBB' },
  { k: 'kc',         label: 'KC' },
  { k: 'supertrend', label: 'SUPERTREND' },
  { k: 'rsi',        label: 'RSI' },
  { k: 'macd',       label: 'MACD' },
  { k: 'atr',        label: 'ATR' },
  { k: 'adx',        label: 'ADX' },
  { k: 'rvol',       label: 'RVOL' },
];

/**
 * ChartToolbar — timeframe + type + indicator toggles.
 * Change dispatch is a single onPrefs callback (prev, patch) => next.
 */
export default function ChartToolbar({ prefs, onPrefs, onResetScale, onOpenColors, onTogglePlayback }) {
  const patch = (obj) => onPrefs?.({ ...prefs, ...obj });
  const toggleInd = (k) => onPrefs?.({
    ...prefs,
    indicators: { ...(prefs.indicators || {}), [k]: !(prefs.indicators || {})[k] },
  });
  const pan2d = !!prefs.pan2d;

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-[var(--border-default)] bg-[var(--bg-panel)] px-2 py-1.5" data-testid="crypto-toolbar">
      <div className="flex items-center gap-1">
        <span className="text-[9px] tracking-[0.35em] uppercase text-[var(--txt-muted)] mr-1">tf</span>
        {TFS.map((tf) => (
          <button
            key={tf}
            data-testid={`crypto-tf-${tf}`}
            onClick={() => patch({ timeframe: tf })}
            className={`px-2 py-0.5 text-[10px] font-mono border ${prefs.timeframe === tf
              ? 'border-cyan-sys text-cyan-sys bg-cyan-sys/10'
              : 'border-[var(--border-default)] text-[var(--txt-secondary)] hover:border-cyan-sys hover:text-cyan-sys'}`}
          >
            {tf}
          </button>
        ))}
      </div>
      <div className="w-px h-4 bg-[var(--border-default)]" />
      <div className="flex items-center gap-1">
        <span className="text-[9px] tracking-[0.35em] uppercase text-[var(--txt-muted)] mr-1">type</span>
        {TYPES.map((t) => (
          <button
            key={t.k}
            data-testid={`crypto-type-${t.k}`}
            onClick={() => patch({ chart_type: t.k })}
            className={`px-2 py-0.5 text-[9px] tracking-widest border ${prefs.chart_type === t.k
              ? 'border-cyan-sys text-cyan-sys bg-cyan-sys/10'
              : 'border-[var(--border-default)] text-[var(--txt-secondary)] hover:border-cyan-sys'}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="w-px h-4 bg-[var(--border-default)]" />
      <button
        onClick={() => patch({ pan2d: !pan2d })}
        data-testid="crypto-pan2d"
        title={pan2d
          ? '2D pan ON — drag the RIGHT PRICE AXIS for vertical scroll/zoom. Double-click axis to auto-fit. Ctrl+scroll disables chart zoom.'
          : '2D pan OFF — enable to drag the right price axis'}
        className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] tracking-widest uppercase border ${pan2d
          ? 'border-cyan-sys text-cyan-sys bg-cyan-sys/10'
          : 'border-[var(--border-default)] text-[var(--txt-secondary)] hover:border-cyan-sys'}`}
      >
        <Move size={11} /> 2d pan
      </button>
      <button
        onClick={() => patch({ ai_overlay: !(prefs.ai_overlay !== false) })}
        data-testid="crypto-ai-overlay"
        title="Toggle AI decision zones + regime overlay on the chart"
        className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] tracking-widest uppercase border ${prefs.ai_overlay !== false
          ? 'border-cyan-sys text-cyan-sys bg-cyan-sys/10'
          : 'border-[var(--border-default)] text-[var(--txt-secondary)] hover:border-cyan-sys'}`}
      >
        <Sparkles size={11} /> ai
      </button>
      {/* Phase 52.1 G3+ — Regime shading toggle (paints _regime_segments) */}
      <button
        onClick={() => patch({ regime_shading: !prefs.regime_shading })}
        data-testid="crypto-regime-shading"
        title="Toggle translucent regime stripes (trending / range / high-vol) behind the candles"
        className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] tracking-widest uppercase border ${prefs.regime_shading
          ? 'border-cyan-sys text-cyan-sys bg-cyan-sys/10'
          : 'border-[var(--border-default)] text-[var(--txt-secondary)] hover:border-cyan-sys'}`}
      >
        <Layers size={11} /> regime
      </button>
      {/* Phase 52.1 Next+ — Session Highlighter (Asia / London / NY) */}
      <button
        onClick={() => patch({ session_overlay: !prefs.session_overlay })}
        data-testid="crypto-session-toggle"
        title="Highlight Asia (00-08 UTC) / London (07-16 UTC) / NY (13-22 UTC) trading sessions. NY session ≈ 7 AM-4 PM Central Time."
        className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] tracking-widest uppercase border ${prefs.session_overlay
          ? 'border-cyan-sys text-cyan-sys bg-cyan-sys/10'
          : 'border-[var(--border-default)] text-[var(--txt-secondary)] hover:border-cyan-sys'}`}
      >
        <Clock size={11} /> sessions
      </button>
      {/* Phase 52.1 Next+ — Playback / Replay mode toggle */}
      <button
        onClick={() => onTogglePlayback?.()}
        data-testid="crypto-playback-toggle"
        title="Playback / Replay mode — scrub history bar-by-bar with every indicator ticking live"
        className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] tracking-widest uppercase border ${prefs.playback_active
          ? 'border-cyan-sys text-cyan-sys bg-cyan-sys/10'
          : 'border-[var(--border-default)] text-[var(--txt-secondary)] hover:border-cyan-sys'}`}
      >
        <Play size={11} /> playback
      </button>
      {/* Phase 52.1 Next+ — Indicator colour palette editor */}
      <button
        onClick={() => onOpenColors?.()}
        data-testid="crypto-colors-open"
        title="Customise DBB / KC / VWAP colours"
        className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] tracking-widest uppercase border border-[var(--border-default)] text-[var(--txt-secondary)] hover:border-cyan-sys hover:text-cyan-sys"
      >
        <Palette size={11} /> colors
      </button>
      {/* Phase 52.1 G3+ — One-click "reset scale" (axis auto-fit) */}
      <button
        onClick={() => onResetScale?.()}
        data-testid="crypto-reset-scale"
        title="Reset chart scale — auto-fit price axis and time range (like TradingView's double-click on the axis)"
        className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] tracking-widest uppercase border border-[var(--border-default)] text-[var(--txt-secondary)] hover:border-cyan-sys hover:text-cyan-sys"
      >
        <Maximize2 size={11} /> reset
      </button>
      <div className="w-px h-4 bg-[var(--border-default)]" />
      <div className="flex flex-wrap items-center gap-1">
        <span className="text-[9px] tracking-[0.35em] uppercase text-[var(--txt-muted)] mr-1">ind</span>
        {IND.map((i) => (
          <button
            key={i.k}
            data-testid={`crypto-ind-${i.k}`}
            onClick={() => toggleInd(i.k)}
            className={`px-2 py-0.5 text-[9px] tracking-widest border ${(prefs.indicators || {})[i.k]
              ? 'border-buy text-buy bg-buy/10'
              : 'border-[var(--border-default)] text-[var(--txt-secondary)] hover:border-buy'}`}
          >
            {i.label}
          </button>
        ))}
      </div>
    </div>
  );
}
